---
title: The task form
lede: A title, an optional prompt, and a row of chips that say how the task runs.
order: 12
part: Run a task
---

The task form is where you describe a change and decide how it runs. Write a title, add a prompt if the title is not enough, then check the chips under it. Click a chip to open its panel; click away to fold it.

![The task form](media/newtask-form.webp)

### Opening it

- **+** in the navigator, then **New task**, **Pull request** or **Investigate clipboard**. **New epic** beside them writes an [epic](/epics) instead.
- `⌘N`, **File › New task…**, or the palette.
- `⌘⇧N` for **New task from pull request…** — see [Pull requests](/pull-requests).
- `⌘⌥N` for **Investigate clipboard…**: a stack trace or bug report on the clipboard becomes a task in the workspace whose files it names. The prompt asks the agent to find the root cause first, then fix it if the bug is in your code.
- **Create › Task** on the [board](/board), for a task that waits there.

### Workspace and priority

The workspace chip appears when you have more than one workspace. It picks whose repositories the task uses, and cannot change once the task is saved.

The priority chip reads **In the queue** or **Urgent**. Urgent starts the task ahead of everything else, even when every session slot and machine is taken.

### Workflow

![The workflow chip](media/newtask-workflow.webp)

| Choice | What happens |
|---|---|
| **One instruction** | The prompt is sent as written; the agent works until it hands back. |
| **Agent proposes** | The agent plans the steps first and waits for you to accept them. |
| **A workflow** | A [workflow](/workflows) from your library runs its steps, one after the other, in the same session. |

**Settings › Sessions › Workflow** sets what new tasks start with.

### Agent, model and effort

![The model chip](media/newtask-model.webp)

The model decides the agent: Claude models run Claude Code, OpenAI models run Codex. Only agents that are switched on and signed in are offered — see [Agents](/agents) — and only the models your organization allows, if you belong to one. **Effort** (**Max**, **X-High**, **High**, **Medium**, **Low**) appears for models that have one.

With a workflow picked, this chip reads **Set per step**: each step names its own model and effort, and the workflow's chip opens the panel, which lists them.

### Where it runs

**Micro-VM** or **This computer**. A task saved for later can also follow **Workspace**. See [Where a session runs](/placement).

### Target

The chip reads **on Local**, or the name of the [target](/targets) picked, and appears when one of your workspaces has targets other than **Local**. It sets the values and hosts the session starts with. A target reached with direct access needs a micro-VM, so **This computer** refuses it.

### Files

Drop or paste files anywhere on the form, or press **Choose files** in the panel. Each file is copied into the sandbox and its path is written into the prompt as an `@` mention. A task carries up to 12 files, 10 MB each and 25 MB in total. A paste of more than 4,000 characters into the prompt is attached as a file too.

A task imported from [Jira or Trello](/integrations) also shows a chip naming its ticket. Its title and text stay the ticket's, shown read only above the editor; what you type goes in your notes, which the sync never touches ([What you add to a card](/integrations)).

### Branches and repositories

![The branches panel](media/newtask-repos.webp)

The last chip names the branch the task is cut from and how many repositories it bundles.

- **Base source** is the branch every repository is cut from.
- **Override** gives one repository its own source branch; **Use base** puts it back.
- A repository without the base branch must get its own source before the task can start. The same holds whichever way the task starts: a card moved to **To do** on the board, an imported ticket or an epic's subtask whose repository lacks its source branch goes back to the backlog, saying which repository.
- Untick a repository to leave it out of the session entirely. The workspace's [rules repository](/rules) cannot be left out.

Picking a [pull request](/pull-requests#several-repositories) sets **Base source** to the branch it targets. Its own repository is cut from the pull request's head, and every other repository either follows the base or gets an override: its own pull request from a branch of the same name, that branch, or its default branch when it has no base. Letting the pull request go puts the base source back.

The agent works on its own branch, `session/<id>`. Where the work lands is asked later, at the [merge](/merging).

### The buttons

| Opened from | Buttons |
|---|---|
| The navigator, `⌘N` or the palette | **Start** runs it now; **Queue** puts it on the board. |
| The board | **Add** puts it on the board. |
| A task not started yet | **Save**. In the backlog, **Run** also saves it and sends it to **To do**, where it starts as soon as there is room. |

**Cancel** closes the form, and **Revert** appears on a written task you changed. `⌘↩` presses the main button and `Esc` closes the form. When something blocks the task (no title, a repository with no source branch, no token for a micro-VM), the foot says why in red and the chip at fault turns red.
